// src/pages/userprofile/UserprofileEditPage.tsx
// Profile edit page — reuses the same layout as ProfileSettingsPage
// but loads profile by route param :id and navigates back to detail view.

import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useUserprofile, useUserprofileAvatar, useUserprofileSetting, useI18n } from '@react/hooks';
import {
  Button, Input, Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter,
  Alert, Badge, ErrorAlert,
} from '@react/components';
import { Textarea } from '@react/components';
import { AvatarUploader } from '../../components/AvatarUploader';
import { cn } from '@core/ui';
import {
  validateProfile, validateAvatar, validateBanner, formatProfileStatus, getProfileInitials,
  genderOptions, bannerGradients,
} from '../../features/userprofile/logic';
import type { Userprofile, Avatar } from '@core/types/userprofile';
import { Select } from '@/components/Select';

// ─── Profile Header (editable) ──────────────────────────────────────

const ProfileHeader = observer(({ profile, avatarUrl, bannerUrl, bannerGradient, onAvatarChange, onBannerChange, onBannerRemove, t }: {
  profile: Userprofile | null;
  avatarUrl: string | undefined;
  bannerUrl: string | undefined;
  bannerGradient: string;
  onAvatarChange: (file: File) => void;
  onBannerChange: (file: File) => void;
  onBannerRemove: () => void;
  t: (key: string) => string;
}) => {
  const initials = profile ? getProfileInitials(profile.first_name || '?', profile.last_name || '?') : '';
  const status = formatProfileStatus(profile?.status ?? 'PROFILE_STATUS_ACTIVE');
  const displayName = profile?.display_name || `${profile?.first_name ?? ''} ${profile?.last_name ?? ''}`.trim();
  const bannerInputRef = useRef<HTMLInputElement>(null);

  return (
    <Card className="overflow-hidden">
      {/* Banner */}
      <div className="relative group/banner">
        {bannerUrl ? (
          <div className="h-32 sm:h-40 overflow-hidden">
            <img src={bannerUrl} alt="" className="w-full h-full object-cover" />
          </div>
        ) : (
          <div className={cn('h-32 sm:h-40 bg-gradient-to-br', bannerGradient)} />
        )}

        {/* Banner controls overlay */}
        <div className="absolute inset-0 flex items-center justify-center gap-2 bg-foreground/0 group-hover/banner:bg-foreground/30 transition-colors">
          <div className="flex gap-2 opacity-0 group-hover/banner:opacity-100 transition-opacity">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => bannerInputRef.current?.click()}
              className="shadow-md"
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {t('userprofile.changeBanner')}
            </Button>
            {bannerUrl && (
              <Button size="sm" variant="danger" onClick={onBannerRemove} className="shadow-md">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </Button>
            )}
          </div>
        </div>

        <input
          ref={bannerInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onBannerChange(f);
            e.target.value = '';
          }}
        />
      </div>

      <div className="px-4 pb-5 sm:px-6 sm:pb-6">
        <div className="flex flex-col sm:flex-row sm:items-end gap-4 -mt-12 sm:-mt-14">
          {/* Avatar */}
          <div className="relative shrink-0 self-center sm:self-auto">
            <AvatarUploader
              currentUrl={avatarUrl}
              onFileSelect={onAvatarChange}
              initials={initials}
              className="!gap-0"
            />
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0 text-center sm:text-left pb-1">
            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
              <h1 className="text-xl sm:text-2xl font-bold text-foreground truncate">
                {displayName || t('userprofile.edit')}
              </h1>
              <Badge variant={status.variant as 'success' | 'warning' | 'error' | 'default'}>
                {t(status.labelKey)}
              </Badge>
            </div>
            {profile?.bio && (
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{profile.bio}</p>
            )}
            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
              {profile?.location && (
                <span className="flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                  {profile.location}
                </span>
              )}
              {profile?.language && (
                <span className="flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" /></svg>
                  {profile.language}
                </span>
              )}
              {profile?.created_at && (
                <span className="flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                  {t('userprofile.memberSince')} {new Date(profile.created_at as string).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
});

// ─── Main Page ───────────────────────────────────────────────────────

export const UserprofileEditPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const profileStore = useUserprofile();
  const avatarStore = useUserprofileAvatar();
  const settingStore = useUserprofileSetting();
  const { t } = useI18n();

  const profile = profileStore.currentItem as Userprofile | null;
  const profileId = id ?? '';

  // ─── Form state ──────────────────────────────
  const [form, setForm] = useState({
    firstName: '', lastName: '', middleName: '', displayName: '',
    bio: '', location: '', gender: '', dateOfBirth: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loaded, setLoaded] = useState(false);

  // ─── Banner state ─────────────────────────────
  const [bannerUrl, setBannerUrl] = useState<string | undefined>();
  const [bannerGradient, setBannerGradient] = useState<string>(bannerGradients[0].value);

  // ─── Avatar state ────────────────────────────
  const [avatars, setAvatars] = useState<Avatar[]>([]);

  // ─── Feedback state ──────────────────────────
  const [success, setSuccess] = useState('');
  const [error, setError] = useState<unknown>(null);

  // ─── Load profile ─────────────────────────────
  useEffect(() => {
    if (profileId) profileStore.GetUserprofile(profileId);
    return () => { profileStore.clearCurrent(); };
  }, [profileId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Populate form from profile ───────────────
  useEffect(() => {
    if (profile && !loaded) {
      setForm({
        firstName: profile.first_name ?? '',
        lastName: profile.last_name ?? '',
        middleName: profile.middle_name ?? '',
        displayName: profile.display_name ?? '',
        bio: profile.bio ?? '',
        location: profile.location ?? '',
        gender: profile.gender ?? '',
        dateOfBirth: profile.date_of_birth ? String(profile.date_of_birth).slice(0, 10) : '',
      });
      if ((profile as unknown as Record<string, unknown>).banner_url) {
        setBannerUrl((profile as unknown as Record<string, unknown>).banner_url as string);
      }
      setLoaded(true);
    }
  }, [profile, loaded]);

  // ─── Load settings + avatars ──────────────────
  useEffect(() => {
    if (!profileId) return;

    settingStore.GetUISettings(profileId)
      .then((resp: { settings?: Record<string, string> }) => {
        if (resp.settings?.banner_gradient) {
          setBannerGradient(resp.settings.banner_gradient);
        }
      })
      .catch(() => {});

    loadAvatars();
  }, [profileId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAvatars = useCallback(async () => {
    if (!profileId) return;
    try {
      await avatarStore.ListAvatars(profileId, { pagination: { limit: 50, cursor: '', direction: '' } } as never);
      setAvatars(avatarStore.items as Avatar[]);
    } catch { /* handled by store */ }
  }, [profileId, avatarStore]);

  // ─── Handlers ────────────────────────────────
  const showSuccess = (key: string) => {
    setSuccess(key);
    setError(null);
  };
  const showError = (err: unknown) => {
    setError(err);
    setSuccess('');
  };

  const handleFormChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleSaveProfile = async () => {
    const validation = validateProfile({ ...form, language: '', timezone: '' });
    if (Object.keys(validation).length > 0) {
      setErrors(Object.fromEntries(Object.entries(validation).map(([k, v]) => [k, t(v)])));
      return;
    }
    try {
      await profileStore.UpdateUserprofile(profileId, {
        userprofile: {
          ...(profile ?? {}),
          first_name: form.firstName, last_name: form.lastName,
          middle_name: form.middleName, display_name: form.displayName,
          bio: form.bio, location: form.location,
          gender: form.gender, date_of_birth: form.dateOfBirth ? `${form.dateOfBirth}T00:00:00Z` : undefined,
        } as never,
      });
      showSuccess('userprofile.saveSuccess');
    } catch (err) { showError(err); }
  };

  const handleSaveAndReturn = async () => {
    const validation = validateProfile({ ...form, language: '', timezone: '' });
    if (Object.keys(validation).length > 0) {
      setErrors(Object.fromEntries(Object.entries(validation).map(([k, v]) => [k, t(v)])));
      return;
    }
    try {
      await profileStore.UpdateUserprofile(profileId, {
        userprofile: {
          ...(profile ?? {}),
          first_name: form.firstName, last_name: form.lastName,
          middle_name: form.middleName, display_name: form.displayName,
          bio: form.bio, location: form.location,
          gender: form.gender, date_of_birth: form.dateOfBirth ? `${form.dateOfBirth}T00:00:00Z` : undefined,
        } as never,
      });
      navigate(`/userprofiles/${profileId}`);
    } catch (err) { showError(err); }
  };

  const handleAvatarUpload = async (file: File) => {
    const validation = validateAvatar(file);
    if (Object.keys(validation).length > 0) {
      setError(t(Object.values(validation)[0]));
      return;
    }
    try {
      const avatar = await avatarStore.CreateAvatar(file);
      if (avatar?.entity?.id) {
        await avatarStore.ActivateAvatar(profileId, avatar.entity.id, { profile: {} as never, avatar: {} as never });
      }
      loadAvatars();
      showSuccess('userprofile.saveSuccess');
    } catch (err) { showError(err); }
  };

  const handleBannerUpload = async (file: File) => {
    const validation = validateBanner(file);
    if (Object.keys(validation).length > 0) {
      setError(t(Object.values(validation)[0]));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setBannerUrl(reader.result as string);
    reader.readAsDataURL(file);

    try {
      await profileStore.UpdateUserprofile(profileId, {
        userprofile: {
          ...(profile ?? {}),
          banner_url: file.name,
        } as never,
      });
      showSuccess('userprofile.bannerSaved');
    } catch (err) { showError(err); }
  };

  const handleBannerRemove = async () => {
    setBannerUrl(undefined);
    try {
      await profileStore.UpdateUserprofile(profileId, {
        userprofile: {
          ...(profile ?? {}),
          banner_url: '',
        } as never,
      });
      showSuccess('userprofile.bannerRemoved');
    } catch (err) { showError(err); }
  };

  const handleActivateAvatar = async (avatarId: string) => {
    try {
      await avatarStore.ActivateAvatar(profileId, avatarId, { profile: {} as never, avatar: {} as never });
      loadAvatars();
    } catch (err) { showError(err); }
  };

  const handleDeleteAvatar = async (avatarId: string) => {
    try {
      await avatarStore.DeleteAvatar(profileId, avatarId);
      setAvatars((prev) => prev.filter((a) => a.entity?.id !== avatarId));
    } catch (err) { showError(err); }
  };

  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  const activeAvatar = avatars.find((a) => a.is_active) ?? avatars[0];
  const activeAvatarUrl = activeAvatar?.url || activeAvatar?.thumbnail_url;

  // ─── Loading state ────────────────────────────
  if (profileStore.isLoadingItem) {
    return (
      <div className="py-16 text-center">
        <div className="inline-block w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        <p className="mt-3 text-sm text-muted-foreground">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Link to={`/userprofiles/${profileId}`} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
          </svg>
          {t('userprofile.view')}
        </Link>
      </div>

      {/* Feedback */}
      {error ? <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} /> : null}
      {success && <Alert variant="success" dismissible onDismiss={() => setSuccess('')}>{t(success)}</Alert>}

      {/* Profile header */}
      <ProfileHeader
        profile={profile}
        avatarUrl={activeAvatarUrl}
        bannerUrl={bannerUrl}
        bannerGradient={bannerGradient}
        onAvatarChange={handleAvatarUpload}
        onBannerChange={handleBannerUpload}
        onBannerRemove={handleBannerRemove}
        t={t}
      />

      {/* Personal Info */}
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('userprofile.nameSection')}</CardTitle>
            <CardDescription>{t('userprofile.nameSectionDesc')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input label={t('userprofile.firstName')} value={form.firstName} onChange={handleFormChange('firstName')} error={errors.firstName} required />
              <Input label={t('userprofile.lastName')} value={form.lastName} onChange={handleFormChange('lastName')} error={errors.lastName} required />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input label={t('userprofile.middleName')} value={form.middleName} onChange={handleFormChange('middleName')} />
              <Input label={t('userprofile.displayName')} value={form.displayName} onChange={handleFormChange('displayName')} error={errors.displayName} placeholder={t('userprofile.displayNamePlaceholder')} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('userprofile.aboutSection')}</CardTitle>
            <CardDescription>{t('userprofile.aboutSectionDesc')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              label={t('userprofile.bio')}
              value={form.bio}
              onChange={handleFormChange('bio')}
              error={errors.bio}
              helperText={`${form.bio.length}/256`}
              rows={4}
              placeholder={t('userprofile.bioPlaceholder')}
            />
            <Input label={t('userprofile.location')} value={form.location} onChange={handleFormChange('location')} placeholder={t('userprofile.locationPlaceholder')} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">{t('userprofile.gender')}</label>
                <Select value={form.gender} onChange={handleFormChange('gender')}>
                  {genderOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                  ))}
                </Select>
              </div>
              <Input label={t('userprofile.dateOfBirth')} type="date" value={form.dateOfBirth} onChange={handleFormChange('dateOfBirth')} />
            </div>
          </CardContent>
          <CardFooter className="flex justify-end gap-2 border-t border-border pt-4">
            <Link to={`/userprofiles/${profileId}`}>
              <Button variant="secondary">{t('common.cancel')}</Button>
            </Link>
            <Button variant="secondary" onClick={handleSaveProfile} isLoading={profileStore.isSaving}>{t('common.saveChanges')}</Button>
            <Button onClick={handleSaveAndReturn} isLoading={profileStore.isSaving}>{t('common.saveAndClose')}</Button>
          </CardFooter>
        </Card>
      </div>

      {/* Avatar Gallery */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">{t('userprofile.avatarGallery')}</CardTitle>
            <CardDescription>{t('userprofile.avatarGalleryDesc')}</CardDescription>
          </div>
          <AvatarUploader
            onFileSelect={handleAvatarUpload}
            className="!gap-0 !flex-row"
          />
        </CardHeader>
        <CardContent>
          {avatars.length === 0 ? (
            <div className="text-center py-8">
              <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
                <svg className="w-6 h-6 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <p className="text-sm text-muted-foreground">{t('userprofile.noAvatars')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
              {avatars.map((avatar) => {
                const imgUrl = avatar.url || avatar.thumbnail_url;
                const sizeKb = avatar.size_bytes ? Math.round(avatar.size_bytes / 1024) : null;
                return (
                  <div key={avatar.entity?.id} className="relative group">
                    <div
                      className={cn(
                        'w-full aspect-square rounded-xl overflow-hidden border-2 transition-all cursor-pointer',
                        avatar.is_active
                          ? 'border-primary ring-2 ring-primary/20'
                          : 'border-transparent hover:border-primary/50',
                      )}
                      onClick={() => imgUrl && setLightboxUrl(imgUrl)}
                    >
                      <img src={imgUrl} alt={avatar.original_filename} className="w-full h-full object-cover" />
                    </div>
                    <div className="absolute inset-0 rounded-xl bg-foreground/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1.5 p-2">
                      <div className="flex gap-1.5">
                        {!avatar.is_active && (
                          <Button size="sm" variant="secondary" onClick={() => handleActivateAvatar(avatar.entity?.id ?? '')} className="text-xs px-2 py-1 h-auto">
                            {t('userprofile.activate')}
                          </Button>
                        )}
                        <Button size="sm" variant="danger" onClick={() => handleDeleteAvatar(avatar.entity?.id ?? '')} aria-label={t('common.delete')} className="px-2 py-1 h-auto">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </Button>
                      </div>
                      {sizeKb && <span className="text-[10px] text-white/80">{sizeKb} KB</span>}
                    </div>
                    {avatar.is_active && (
                      <div className="absolute -top-1.5 -right-1.5 flex items-center justify-center w-5 h-5 bg-primary rounded-full border-2 border-card">
                        <svg className="w-3 h-3 text-primary-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                    <p className="mt-1 text-[11px] text-muted-foreground truncate text-center" title={avatar.original_filename}>
                      {avatar.original_filename || avatar.entity?.id?.slice(0, 8)}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lightbox */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setLightboxUrl(null)}
        >
          <button
            onClick={() => setLightboxUrl(null)}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <img
            src={lightboxUrl}
            alt="Preview"
            className="max-w-[90vw] max-h-[90vh] rounded-lg shadow-2xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
});

export default UserprofileEditPage;
